/**
 * CourtyardPage — Scene Setter
 *
 * Scrolling narrative introducing the three Greek guides.
 * Each section fades in as the user scrolls. On mount, marks the
 * scene setter as seen so the dashboard CTA disappears.
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

import { SCENE_SECTIONS, SCENE_TITLE } from '../data/sceneSetter';
import CharacterDialogue from '../components/characters/CharacterDialogue';
import { useProgress } from '../context/ProgressContext';
import { useGamification } from '../context/GamificationContext';
import Button from '../components/shared/Button';
import CourtyardScene, { hasCourtyardScene } from '../components/art/CourtyardScene';

/**
 * Parse a content line and decide whether it is dialogue, narration, or prose.
 * Dialogue lines follow the pattern "CharacterName: \"...\""
 */
const parseLine = (line) => {
  const dialogueMatch = line.match(/^(Philemon|Xenia|Theron):\s*"?(.*?)"?$/);
  if (dialogueMatch) {
    return { type: 'dialogue', character: dialogueMatch[1], text: dialogueMatch[2] };
  }
  if (line.startsWith('*') && line.endsWith('*')) {
    return { type: 'narration', text: line.slice(1, -1) };
  }
  return { type: 'prose', text: line };
};

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: 'easeOut' },
  },
};

const CourtyardPage = () => {
  const { markSceneSetterSeen, getAchievementContext } = useProgress();
  const { checkAchievements } = useGamification();

  useEffect(() => {
    markSceneSetterSeen();
    // State commit is async; assert the fact we just established
    checkAchievements(getAchievementContext({ hasSeenSceneSetter: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>The Courtyard | Finding Your Voice</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-courtyard-parchment via-courtyard-parchment to-courtyard-stone/10">
        <div className="max-w-3xl mx-auto px-4 py-12 space-y-16">
          {/* Title */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <h1 className="font-display text-4xl md:text-5xl font-bold text-courtyard-stone">
              {SCENE_TITLE}
            </h1>
            {hasCourtyardScene() && (
              <div className="mt-8 rounded-2xl overflow-hidden border border-courtyard-gold/40 shadow-courtyard-md">
                <CourtyardScene eager sizes="(min-width: 768px) 736px, 100vw" />
              </div>
            )}
          </motion.div>

          {/* Sections */}
          {SCENE_SECTIONS.map((section) => (
            <motion.section
              key={section.id}
              className="space-y-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >
              {section.title && (
                <h2 className="font-display text-xl font-bold text-courtyard-stone/80">
                  {section.title}
                </h2>
              )}

              {section.content.map((line, i) => {
                const parsed = parseLine(line);

                if (parsed.type === 'dialogue') {
                  return (
                    <CharacterDialogue
                      key={i}
                      characterId={parsed.character.toLowerCase()}
                      speaker={parsed.character}
                      text={parsed.text}
                    />
                  );
                }

                if (parsed.type === 'narration') {
                  return (
                    <p
                      key={i}
                      className="text-courtyard-stone/80 italic leading-relaxed text-center"
                    >
                      {parsed.text}
                    </p>
                  );
                }

                return (
                  <p key={i} className="text-courtyard-stone/80 leading-relaxed">
                    {parsed.text}
                  </p>
                );
              })}
            </motion.section>
          ))}

          {/* CTA */}
          <motion.div
            className="text-center pt-8 pb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={sectionVariants}
          >
            <Link to="/assessment">
              <Button variant="primary" size="lg">
                Begin Assessment
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default CourtyardPage;
